/**
 * Codes d'erreur API partagés entre le backend et le frontend
 * Source unique de vérité pour tous les codes d'erreur de l'application
 *
 * Usage Backend (NestJS):
 * ```typescript
 * import { ERROR_CODES } from '@repo/contracts/errors';
 * throw new Error(ERROR_CODES.TEMPLATE_ERROR_PARSING);
 * ```
 *
 * Usage Frontend:
 * ```typescript
 * import { ERROR_CODES } from '@repo/contracts/errors';
 * export const GLOBAL_ERRORS = {
 *   [ERROR_CODES.TEMPLATE_ERROR_PARSING]: { messageKey: '...', severity: 'error' }
 * };
 * ```
 */

export const ERROR_CODES = {
  // ========================================
  // 1️⃣  AUTHENTICATION & AUTHORIZATION
  // ========================================
  WRONG_AUTH: 'WRONG_AUTH',
  WRONG_CONFIG_COMPANY: 'WRONG_CONFIG_COMPANY',
  UNAUTHORIZED: 'UNAUTHORIZED',
  UNAUTHORIZED_01: 'UNAUTHORIZED:01',
  UNAUTHORIZED_02: 'UNAUTHORIZED:02',
  UNAUTHORIZED_03: 'UNAUTHORIZED:03',
  UNAUTHORIZED_04: 'UNAUTHORIZED:04',
  UNAUTHORIZED_05: 'UNAUTHORIZED:05',
  UNAUTHORIZED_06: 'UNAUTHORIZED:06',
  UNAUTHORIZED_11: 'UNAUTHORIZED:11',
  UNAUTHORIZED_12: 'UNAUTHORIZED:12',
  UNAUTHORIZED_13: 'UNAUTHORIZED:13',
  UNAUTHORIZED_14: 'UNAUTHORIZED:14',
  UNAUTHORIZED_15: 'UNAUTHORIZED:15',
  INVALID_TOKEN: 'INVALID_TOKEN',
  INACTIVE_COMPANY: 'INACTIVE_COMPANY',
  NO_LICENSE: 'NO_LICENSE',

  // ========================================
  // 2️⃣  DT - ENRICHISSEMENT PROFIL
  // ========================================
  // Templates & Files
  DT_TEMPLATE_NOT_FOUND: 'DT_TEMPLATE_NOT_FOUND',
  DT_TEMPLATE_FILE_INVALID_FILE_TYPE: 'DT_TEMPLATE_FILE_INVALID_FILE_TYPE',
  DT_TEMPLATE_FILE_NOT_FOUND: 'DT_TEMPLATE_FILE_NOT_FOUND',

  // Import/Export
  DT_IMPORT_DT_REFERENCES_DESCRIPTIONS_ALL_EMPTY: 'DT_IMPORT_DT_REFERENCES_DESCRIPTIONS_ALL_EMPTY',
  DT_EXPORT_DT_NOT_FOUND: 'DT_EXPORT_DT_NOT_FOUND',
  NEYLIA_REFERENTIALS_BLOCK_BOOND_EXPORT: 'NEYLIA_REFERENTIALS_BLOCK_BOOND_EXPORT',
  DT_NOT_FOUND: 'DT_NOT_FOUND',
  DT_IMPROVE_DT_NOT_FOUND: 'DT_IMPROVE_DT_NOT_FOUND',
  DT_REVISION_NOT_FOUND_OR_UPDATE_FAILED: 'DT_REVISION_NOT_FOUND_OR_UPDATE_FAILED',

  // Targets Configuration
  SUMMARY_TARGET_NOT_CONFIGURED: 'SUMMARY_TARGET_NOT_CONFIGURED',
  SUMMARY_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'SUMMARY_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  STRENGTHS_TARGET_NOT_CONFIGURED: 'STRENGTHS_TARGET_NOT_CONFIGURED',
  STRENGTHS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'STRENGTHS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  SOFTSKILLS_TARGET_NOT_CONFIGURED: 'SOFTSKILLS_TARGET_NOT_CONFIGURED',
  SOFTSKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'SOFTSKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  SKILLS_TARGET_NOT_CONFIGURED: 'SKILLS_TARGET_NOT_CONFIGURED',
  SKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'SKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  MISCELLANEOUS_TARGET_NOT_CONFIGURED: 'MISCELLANEOUS_TARGET_NOT_CONFIGURED',
  MISCELLANEOUS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'MISCELLANEOUS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  DT_SUMMARY_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'DT_SUMMARY_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  DT_STRENGTHS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'DT_STRENGTHS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  DT_SOFTSKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'DT_SOFTSKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  DT_SKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'DT_SKILLS_TARGET_ACTION_TYPE_NOT_CONFIGURED',
  DT_MISCELLANEOUS_TARGET_ACTION_TYPE_NOT_CONFIGURED: 'DT_MISCELLANEOUS_TARGET_ACTION_TYPE_NOT_CONFIGURED',

  // AI Actions & Processing
  DT_ERP_INFORMATION_COMMENTS_TOO_LONG: 'DT_ERP_INFORMATION_COMMENTS_TOO_LONG',

  // Translations & Sources
  DT_TRANSLATE_SOURCE_NOT_FOUND: 'DT_TRANSLATE_SOURCE_NOT_FOUND',

  // ========================================
  // 3️⃣  OPPORTUNITY - FICHE DE POSTE
  // ========================================
  // Templates
  OPPORTUNITY_TEMPLATE_NOT_FOUND: 'OPPORTUNITY_TEMPLATE_NOT_FOUND',
  OPPORTUNITY_TEMPLATE_FILE_INVALID_FILE_TYPE: 'OPPORTUNITY_TEMPLATE_FILE_INVALID_FILE_TYPE',

  // Core Entity
  OPPORTUNITY_NOT_FOUND: 'OPPORTUNITY_NOT_FOUND',
  OPPORTUNITY_ALREADY_EXISTS: 'OPPORTUNITY_ALREADY_EXISTS',

  // Translations
  OPPORTUNITY_TRANSLATE_SOURCE_NOT_FOUND: 'OPPORTUNITY_TRANSLATE_SOURCE_NOT_FOUND',

  // ========================================
  // 4️⃣  INTERVIEW-PREPARATION - ETUDE CANDIDATURE
  // ========================================
  // Core Entity
  INTERVIEW_PREPARATION_NOT_FOUND: 'INTERVIEW_PREPARATION_NOT_FOUND',

  // Reviews
  INTERVIEW_PREPARATION_REVIEW_NOT_FOUND: 'INTERVIEW_PREPARATION_REVIEW_NOT_FOUND',
  INTERVIEW_PREPARATION_REVIEWS_NOT_FOUND: 'INTERVIEW_PREPARATION_REVIEWS_NOT_FOUND',
  INTERVIEW_PREPARATION_REVIEW_POSITIONING_NOT_FOUND: 'INTERVIEW_PREPARATION_REVIEW_POSITIONING_NOT_FOUND',

  // Templates
  INTERVIEW_PREPARATION_TEMPLATE_NOT_FOUND: 'INTERVIEW_PREPARATION_TEMPLATE_NOT_FOUND',

  // Positionings
  POSITIONING_NOT_FOUND: 'POSITIONING_NOT_FOUND',
  REFUSAL_POSITIONING_STATE_NOT_CONFIGURED: 'REFUSAL_POSITIONING_STATE_NOT_CONFIGURED',

  // Action Types
  ACTION_TYPE_SAVE_NOT_FOUND: 'ACTION_TYPE_SAVE_NOT_FOUND',

  // Business Logic
  UNKNOWN_MODE_FOR_INTERVIEW_PREPARATION_CREATION: 'UNKNOWN_MODE_FOR_INTERVIEW_PREPARATION_CREATION',

  // ========================================
  // 5️⃣  OUTBOUND
  // ========================================
  INSEE_COMPANIES_ERROR: 'INSEE_COMPANIES_ERROR',
  REGISTRY_SEARCH_INVALID_INPUT: 'REGISTRY_SEARCH_INVALID_INPUT',
  REGISTRY_SEARCH_TIMEOUT: 'REGISTRY_SEARCH_TIMEOUT',
  REGISTRY_SEARCH_UNAVAILABLE: 'REGISTRY_SEARCH_UNAVAILABLE',
  REGISTRY_SEARCH_TECHNICAL_ERROR: 'REGISTRY_SEARCH_TECHNICAL_ERROR',
  CONTACT_PERSON_QUERY_INVALID: 'CONTACT_PERSON_QUERY_INVALID',
  CONTACT_PERSON_NOT_FOUND: 'CONTACT_PERSON_NOT_FOUND',
  EMAIL_CHANNEL_PROVIDER_NOT_CONFIGURED: 'EMAIL_CHANNEL_PROVIDER_NOT_CONFIGURED',
  EMAIL_CHANNEL_M365_CONFIG_MISSING: 'EMAIL_CHANNEL_M365_CONFIG_MISSING',
  EMAIL_CHANNEL_SENDER_REQUIRED: 'EMAIL_CHANNEL_SENDER_REQUIRED',
  EMAIL_CHANNEL_THREAD_REFERENCE_INVALID: 'EMAIL_CHANNEL_THREAD_REFERENCE_INVALID',
  EMAIL_CHANNEL_MESSAGE_NOT_FOUND: 'EMAIL_CHANNEL_MESSAGE_NOT_FOUND',
  EMAIL_CHANNEL_UNAUTHORIZED: 'EMAIL_CHANNEL_UNAUTHORIZED',
  EMAIL_CHANNEL_RATE_LIMIT: 'EMAIL_CHANNEL_RATE_LIMIT',
  EMAIL_CHANNEL_UNAVAILABLE: 'EMAIL_CHANNEL_UNAVAILABLE',
  EMAIL_CHANNEL_SEND_FAILED: 'EMAIL_CHANNEL_SEND_FAILED',
  CONNECTED_ACCOUNT_REQUIRED: 'CONNECTED_ACCOUNT_REQUIRED',
  CONNECTED_ACCOUNT_SCOPE_MISSING: 'CONNECTED_ACCOUNT_SCOPE_MISSING',
  CONNECTED_ACCOUNT_CONSENT_DENIED: 'CONNECTED_ACCOUNT_CONSENT_DENIED',
  CONNECTED_ACCOUNT_ADMIN_CONSENT_REQUIRED: 'CONNECTED_ACCOUNT_ADMIN_CONSENT_REQUIRED',
  CONNECTED_ACCOUNT_PROVIDER_POLICY_BLOCKED: 'CONNECTED_ACCOUNT_PROVIDER_POLICY_BLOCKED',
  CONNECTED_ACCOUNT_NEEDS_RECONNECT: 'CONNECTED_ACCOUNT_NEEDS_RECONNECT',
  CONNECTED_ACCOUNT_OAUTH_STATE_INVALID: 'CONNECTED_ACCOUNT_OAUTH_STATE_INVALID',
  CONNECTED_ACCOUNT_MAILBOX_UNAVAILABLE: 'CONNECTED_ACCOUNT_MAILBOX_UNAVAILABLE',
  CONNECTED_ACCOUNT_PROVIDER_CAPABILITY_UNAVAILABLE: 'CONNECTED_ACCOUNT_PROVIDER_CAPABILITY_UNAVAILABLE',

  // ========================================
  // 5️⃣  BOOND - INTEGRATION (Provider Central)
  // ========================================
  // Entities
  BOOND_ENTITY_NOT_FOUND: 'BOOND_ENTITY_NOT_FOUND',
  BOOND_ENTITY_NOT_FOUND_AND_CLEANED: 'BOOND_ENTITY_NOT_FOUND_AND_CLEANED',
  BOOND_ENTITY_DOCUMENTS_NOT_FOUND: 'BOOND_ENTITY_DOCUMENTS_NOT_FOUND',
  BOOND_ENTITY_TECHNICAL_DATA_NOT_FOUND: 'BOOND_ENTITY_TECHNICAL_DATA_NOT_FOUND',
  BOOND_ENTITY_POSITIONINGS_LOOP_LIMIT: 'BOOND_ENTITY_POSITIONINGS_LOOP_LIMIT',

  // Candidates
  BOOND_CANDIDATES_LOOP_LIMIT: 'BOOND_CANDIDATES_LOOP_LIMIT',
  BOOND_CANDIDATE_INFORMATION_NOT_FOUND: 'BOOND_CANDIDATE_INFORMATION_NOT_FOUND',
  BOOND_CANDIDATE_NOT_FOUND_AND_CLEANED: 'BOOND_CANDIDATE_NOT_FOUND_AND_CLEANED',
  BOOND_CANDIDATE_TECHNICAL_DATA_NOT_FOUND: 'BOOND_CANDIDATE_TECHNICAL_DATA_NOT_FOUND',
  BOOND_CANDIDATE_POSITIONINGS_LOOP_LIMIT: 'BOOND_CANDIDATE_POSITIONINGS_LOOP_LIMIT',
  BOOND_CANDIDATE_ACTIONS_LOOP_LIMIT: 'BOOND_CANDIDATE_ACTIONS_LOOP_LIMIT',

  // Resources
  BOOND_RESOURCES_LOOP_LIMIT: 'BOOND_RESOURCES_LOOP_LIMIT',
  BOOND_RESOURCE_NOT_FOUND_AND_CLEANED: 'BOOND_RESOURCE_NOT_FOUND_AND_CLEANED',
  BOOND_RESOURCE_TECHNICAL_DATA_NOT_FOUND: 'BOOND_RESOURCE_TECHNICAL_DATA_NOT_FOUND',
  BOOND_RESOURCE_POSITIONINGS_LOOP_LIMIT: 'BOOND_RESOURCE_POSITIONINGS_LOOP_LIMIT',
  BOOND_RESOURCE_ACTIONS_LOOP_LIMIT: 'BOOND_RESOURCE_ACTIONS_LOOP_LIMIT',

  // Opportunities
  BOOND_OPPORTUNITY_NOT_FOUND: 'BOOND_OPPORTUNITY_NOT_FOUND',
  BOOND_OPPORTUNITY_NOT_FOUND_AND_CLEANED: 'BOOND_OPPORTUNITY_NOT_FOUND_AND_CLEANED',
  BOOND_OPPORTUNITIES_NOT_FOUND: 'BOOND_OPPORTUNITIES_NOT_FOUND',
  BOOND_OPPORTUNITY_POSITIONINGS_LOOP_LIMIT: 'BOOND_OPPORTUNITY_POSITIONINGS_LOOP_LIMIT',
  BOOND_OPPORTUNITY_ACTIONS_LOOP_LIMIT: 'BOOND_OPPORTUNITY_ACTIONS_LOOP_LIMIT',

  // Contacts
  BOOND_COMPANY_CONTACTS_LOOP_LIMIT: 'BOOND_COMPANY_CONTACTS_LOOP_LIMIT',
  BOOND_CONTACT_ACTIONS_LOOP_LIMIT: 'BOOND_CONTACT_ACTIONS_LOOP_LIMIT',

  // Projects & Orders
  BOOND_COMPANY_PROJECTS_LOOP_LIMIT: 'BOOND_COMPANY_PROJECTS_LOOP_LIMIT',
  BOOND_PROJECT_ORDERS_LOOP_LIMIT: 'BOOND_PROJECT_ORDERS_LOOP_LIMIT',

  // Dictionaries & Configuration
  BOOND_DICTIONARY_SETTINGS_NOT_FOUND: 'BOOND_DICTIONARY_SETTINGS_NOT_FOUND',
  BOOND_DICTIONARY_NOT_FOUND: 'BOOND_DICTIONARY_NOT_FOUND',
  BOOND_BACKUP_ACTION_TYPE_NOT_FOUND: 'BOOND_BACKUP_ACTION_TYPE_NOT_FOUND',

  // Documents & Technical Data
  BOOND_DOCUMENT_NOT_FOUND: 'BOOND_DOCUMENT_NOT_FOUND',

  // User Tokens & Authentication
  BOOND_USER_TOKEN_NOT_FOUND: 'BOOND_USER_TOKEN_NOT_FOUND',

  // Manager Loop Limits
  BOOND_MANAGERS_LOOP_LIMIT: 'BOOND_MANAGERS_LOOP_LIMIT',

  // API Errors
  BOOND_API_ERROR: 'BOOND_API_ERROR',

  // Configuration & Setup
  BOOND_API_URL_NOT_CONFIGURED: 'BOOND_API_URL_NOT_CONFIGURED',

  // ========================================
  // 6️⃣  AI & LANGUAGE PROCESSING (Transversal)
  // ========================================
  // Credits & Configuration
  NO_CREDITS: 'NO_CREDITS',
  AI_ACTIVE_CONFIG_NOT_FOUND: 'AI_ACTIVE_CONFIG_NOT_FOUND',
  ACTIVE_AI_CONFIG_NOT_FOUND: 'ACTIVE_AI_CONFIG_NOT_FOUND',

  // Requests
  AI_REQUEST_NOT_FOUND: 'AI_REQUEST_NOT_FOUND',
  AI_REQUEST_NO_CREDITS: 'AI_REQUEST_NO_CREDITS',

  // Prompts
  AI_PROMPT_NOT_FOUND: 'AI_PROMPT_NOT_FOUND',
  AI_PROMPT_TOO_LARGE: 'AI_PROMPT_TOO_LARGE',
  AI_PROMPT_TOO_LARGE_INCOMPLETE_WEBSETTINGS: 'AI_PROMPT_TOO_LARGE_INCOMPLETE_WEBSETTINGS',
  AI_PROMPT_TOO_LARGE_CONFIG_ACTIONS: 'AI_PROMPT_TOO_LARGE_CONFIG_ACTIONS',

  // Completions & Parsing
  AI_COMPLETION_FAILED: 'AI_COMPLETION_FAILED',
  AI_PARSE_FAILED: 'AI_PARSE_FAILED',

  // Actions
  AI_ACTION_NOT_FOUND: 'AI_ACTION_NOT_FOUND',
  AI_ACTION_TRANSLATE_TEXT_REQUIRED: 'AI_ACTION_TRANSLATE_TEXT_REQUIRED',
  AI_ACTION_TRANSLATE_TARGET_LANGUAGE_REQUIRED: 'AI_ACTION_TRANSLATE_TARGET_LANGUAGE_REQUIRED',
  AI_ACTION_TRANSLATE_TARGET_LANGUAGE_EQUALS_SOURCE_LANGUAGE: 'AI_ACTION_TRANSLATE_TARGET_LANGUAGE_EQUALS_SOURCE_LANGUAGE',

  // LLM Errors
  AI_TIMEOUT: 'AI_TIMEOUT',
  AI_UNKNOWN_ERROR: 'AI_UNKNOWN_ERROR',
  AI_SERVER_ERROR: 'AI_SERVER_ERROR',
  AI_RATE_LIMIT: 'AI_RATE_LIMIT',
  AI_RESPONSE_INVALID_WEIGHTS: 'AI_RESPONSE_INVALID_WEIGHTS',
  AI_INVALID_REQUEST: 'AI_INVALID_REQUEST',

  // ========================================
  // 7️⃣  SHARED LINKS
  // ========================================
  SHARED_LINK_FORBIDDEN: 'SHARED_LINK_FORBIDDEN',

  // ========================================
  // 8️⃣  COMMON INFRASTRUCTURE
  // ========================================
  // Permissions & Access Control
  FORBIDDEN_403: 'FORBIDDEN_403',
  STATUS_403: '403_STATUS',
  FORBIDDEN: 'FORBIDDEN',
  PLAN_LOCKED: 'PLAN_LOCKED',
  USED: 'USED',
  TECHNICAL_CONTACT_MISSING: 'TECHNICAL_CONTACT_MISSING',

  // Resources - Not Found
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
  ROLE_NOT_FOUND: 'ROLE_NOT_FOUND',
  ENTITY_TYPE_NOT_FOUND: 'ENTITY_TYPE_NOT_FOUND',
  DASHBOARD_NOT_FOUND: 'DASHBOARD_NOT_FOUND',

  // Conflicts & Duplications
  SIRET_ALREADY_EXISTS: 'SIRET_ALREADY_EXISTS',
  DUNS_ALREADY_EXISTS: 'DUNS_ALREADY_EXISTS',
  RCS_ALREADY_EXISTS: 'RCS_ALREADY_EXISTS',
  TVA_IC_ALREADY_EXISTS: 'TVA_IC_ALREADY_EXISTS',
  ID_ALREADY_EXISTS: 'ID_ALREADY_EXISTS',
  LEVEL_ALREADY_EXISTS: 'LEVEL_ALREADY_EXISTS',
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  TYPE_CONTACT_ALREADY_EXISTS: 'TYPE_CONTACT_ALREADY_EXISTS',
  ANNOUNCEMENT_ALREADY_EXISTS: 'ANNOUNCEMENT_ALREADY_EXISTS',
  COMPANY_DELETE_LOCKED_DUE_TO_ORDER_ITEMS: 'COMPANY_DELETE_LOCKED_DUE_TO_ORDER_ITEMS',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  ALREADY_ASSIGNED: 'ALREADY_ASSIGNED',

  // Subscriptions & Licenses
  NO_VALID_SUBSCRIPTION: 'NO_VALID_SUBSCRIPTION',
  NO_LICENSE_TO_END: 'NO_LICENSE_TO_END',
  ASSIGNED_LICENSE: 'ASSIGNED_LICENSE',
  COMMITMENT_CHANGE_ALREADY_IN_PROGRESS: 'COMMITMENT_CHANGE_ALREADY_IN_PROGRESS',

  // Files & Storage
  FILE_NOT_FOUND: 'FILE_NOT_FOUND',
  FILE_REQUIRED: 'FILE_REQUIRED',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  INVALID_FILE_SIZE: 'INVALID_FILE_SIZE',
  MISTRAL_FILE_UPLOAD_FAILED: 'MISTRAL_FILE_UPLOAD_FAILED',
  MISTRAL_FILE_UPLOAD_SERVER_ERROR: 'MISTRAL_FILE_UPLOAD_SERVER_ERROR',
  MISTRAL_FILE_URL_FAILED: 'MISTRAL_FILE_URL_FAILED',
  MISTRAL_FILE_CLEANUP_FAILED: 'MISTRAL_FILE_CLEANUP_FAILED',

  // Templates & Processing
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  TEMPLATE_ERROR_PARSING: 'TEMPLATE_ERROR_PARSING',
  TEMPLATE_FILE_INVALID_FILE_TYPE: 'TEMPLATE_FILE_INVALID_FILE_TYPE',
  TEMPLATE_FILE_MISSING_ON_DISK: 'TEMPLATE_FILE_MISSING_ON_DISK',
  UNCLOSED_TAG: 'UNCLOSED_TAG',

  // Validation & Constraints
  MISSING_TYPE: 'MISSING_TYPE',
  COMPANY_REQUIRED: 'COMPANY_REQUIRED',
  QUOTA_VALUE_INVALID: 'QUOTA_VALUE_INVALID',
  LINK_PLAN_NOT_FOUND: 'LINK_PLAN_NOT_FOUND',
  CANNOT_LINK_TO_ITSELF: 'CANNOT_LINK_TO_ITSELF',

  // Mailing
  MAILING_LIST_ALREADY_EXISTS: 'MAILING_LIST_ALREADY_EXISTS',

  // Cron & Scheduled Tasks
  HANDLER_KEY_ALREADY_USED: 'HANDLER_KEY_ALREADY_USED',
  INVALID_CRON_EXPRESSION: 'INVALID_CRON_EXPRESSION',
  INVALID_HANDLER_KEY: 'INVALID_HANDLER_KEY',
  ALREADY_RUNNING: 'ALREADY_RUNNING',

  // Generic Resources
  NOT_FOUND: 'NOT_FOUND',

  // Other Infrastructure
  DOWNLOAD_ERROR: 'DOWNLOAD_ERROR',
  VERSION_NOT_FOUND: 'VERSION_NOT_FOUND',
  SPECIFICATION_NOT_FOUND: 'SPECIFICATION_NOT_FOUND',
} as const;

/**
 * Liste de tous les codes d'erreur (utile pour validation ou génération)
 */
export const ALL_ERROR_CODES = Object.values(ERROR_CODES);
